import { defineStore } from 'pinia';
import { markRaw, ref, shallowReactive } from 'vue';
import type { Id } from '@vp/shared-types';
import { copyCanvasParts, DATA_FORMAT_VERSION, ulid, type CanvasMetaExt } from '@vp/core';
import { forgetCanvasOpen, noteCanvasOpen } from '../canvas-use';
import { forgetCanvasChat } from '../chat';
import { logger, reportStorageError, storage } from '../platform';
import { CanvasSession, type SessionHooks } from '../session';
import { t } from '../i18n';

export type View = 'home' | 'editor' | 'modules' | 'settings' | 'logs' | 'profile' | 'cards';

export interface Toast {
  id: number;
  level: 'info' | 'warning' | 'error';
  message: string;
  /** Buttons on the message (module notifications, API 2.2); pressing one closes it. */
  actions?: { label: string; run: () => void }[];
}

let toastId = 0;

export const useApp = defineStore('app', () => {
  const view = ref<View>('home');
  const canvases = ref<CanvasMetaExt[]>([]);
  const canvasesLoaded = ref(false);
  const tabs = ref<Id[]>([]);
  const active = ref<Id | null>(null);
  const sessions = shallowReactive(new Map<Id, CanvasSession>());
  const toasts = ref<Toast[]>([]);
  /** Slide-out navigation drawer. */
  const drawerOpen = ref(false);
  /** Settings section to open next (modules live inside Settings). */
  const settingsSection = ref<string | null>(null);
  let hooks: SessionHooks = {};
  /** Runs before a canvas session is closed (e.g. refresh its preview). */
  let beforeClose: ((s: CanvasSession) => Promise<void>) | null = null;
  /** Set once the user navigated; startup hash restore must not override it. */
  let navigated = false;

  function setSessionHooks(h: SessionHooks): void {
    hooks = h;
  }

  function setBeforeClose(fn: (s: CanvasSession) => Promise<void>): void {
    beforeClose = fn;
  }

  function notify(level: Toast['level'], message: string, ms = 5000, actions?: Toast['actions']): void {
    const id = ++toastId;
    toasts.value.push({ id, level, message, ...(actions?.length ? { actions } : {}) });
    // a message with buttons stays longer: there is something to press
    if (actions?.length) ms = Math.max(ms, 10000);
    if (toasts.value.length > 5) toasts.value.shift();
    setTimeout(() => dismiss(id), level === 'error' ? ms * 2 : ms);
  }

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((x) => x.id !== id);
  }

  async function refreshCanvases(): Promise<void> {
    canvases.value = await storage.listCanvases();
    canvasesLoaded.value = true;
  }

  function newMeta(name: string): CanvasMetaExt {
    const now = Date.now();
    return { id: ulid(), name, createdAt: now, updatedAt: now, formatVersion: DATA_FORMAT_VERSION, lastExportAt: null, objectCount: 0 };
  }

  async function createCanvas(name?: string): Promise<Id> {
    const meta = newMeta(name || t('home.untitled'));
    await storage.putCanvas(meta);
    logger.info('canvas.create', { canvasId: meta.id });
    await refreshCanvases();
    await openCanvas(meta.id);
    return meta.id;
  }

  async function openCanvas(id: Id, fromHash = false): Promise<void> {
    if (!fromHash) navigated = true;
    if (!tabs.value.includes(id)) tabs.value.push(id);
    active.value = id;
    view.value = 'editor';
    if (!sessions.has(id)) {
      try {
        const s = await CanvasSession.open(id, { ...hooks, onNotify: notify }, { w: window.innerWidth, h: window.innerHeight });
        sessions.set(id, markRaw(s));
      } catch (e) {
        logger.error('canvas.open.failed', { canvasId: id, error: e instanceof Error ? e.name : 'unknown' }, );
        console.error('openCanvas failed', e);
        notify('error', t('error.unknown', { msg: e instanceof Error ? e.message : String(e) }));
        tabs.value = tabs.value.filter((x) => x !== id);
        active.value = tabs.value[0] ?? null;
        if (!active.value) view.value = 'home';
        syncHash();
        return;
      }
    }
    noteCanvasOpen(id);
    syncHash();
  }

  async function closeTab(id: Id): Promise<void> {
    const s = sessions.get(id);
    if (s) await beforeClose?.(s).catch(() => undefined);
    sessions.delete(id);
    const idx = tabs.value.indexOf(id);
    tabs.value = tabs.value.filter((x) => x !== id);
    if (active.value === id) active.value = tabs.value[Math.max(0, idx - 1)] ?? null;
    if (!active.value && view.value === 'editor') view.value = 'home';
    await s?.dispose();
    await refreshCanvases();
    syncHash();
  }

  /** Leaving the editor closes every open canvas (saved first). */
  async function closeAll(): Promise<void> {
    const ids = [...tabs.value];
    for (const id of ids) {
      const s = sessions.get(id);
      if (s) await beforeClose?.(s).catch(() => undefined);
    }
    // the user came back to the editor meanwhile: keep it open
    if (view.value === 'editor') return;
    tabs.value = tabs.value.filter((x) => !ids.includes(x));
    if (active.value && ids.includes(active.value)) active.value = tabs.value[0] ?? null;
    for (const id of ids) {
      const s = sessions.get(id);
      sessions.delete(id);
      await s?.dispose();
    }
    await refreshCanvases();
  }

  async function renameCanvas(id: Id, name: string): Promise<void> {
    await storage.updateCanvas(id, { name });
    const s = sessions.get(id);
    if (s) s.meta.value = { ...s.meta.value, name };
    await refreshCanvases();
  }

  /** Solid color cover shown on "My canvases" instead of the preview (null = the preview). */
  async function setCanvasCover(id: Id, cover: string | null): Promise<void> {
    const meta = await storage.getCanvas(id);
    if (!meta) return;
    const settings = { ...(meta.settings ?? {}) };
    if (cover) settings['cover'] = cover;
    else delete settings['cover'];
    await storage.updateCanvas(id, { settings });
    const s = sessions.get(id);
    if (s) s.meta.value = { ...s.meta.value, settings };
    await refreshCanvases();
  }

  /** A setting kept with the canvas (bookmarks…); null removes it. */
  async function setCanvasSetting(id: Id, key: string, value: unknown): Promise<void> {
    const meta = await storage.getCanvas(id);
    if (!meta) return;
    const settings = { ...(meta.settings ?? {}) };
    if (value === null || value === undefined) delete settings[key];
    else settings[key] = JSON.parse(JSON.stringify(value));
    try {
      await storage.updateCanvas(id, { settings });
    } catch (e) {
      reportStorageError(e);
      throw e;
    }
    const s = sessions.get(id);
    if (s) s.meta.value = { ...s.meta.value, settings };
  }

  /** Hooks of open sessions, also used to draw previews of canvases that are not open. */
  function sessionHooks(): SessionHooks {
    return hooks;
  }

  async function duplicateCanvas(id: Id): Promise<Id> {
    await sessions.get(id)?.flush();
    const src = await storage.getCanvas(id);
    const { objects, links } = await storage.readCanvasContent(id);
    const meta = newMeta((src?.name ?? t('home.untitled')) + ' (2)');
    const objs = objects.map((o) => ({ ...o, canvasId: meta.id }));
    const ls = links.map((l) => ({ ...l, canvasId: meta.id }));
    await storage.createCanvasWithContent(meta, objs, ls);
    // the canvas parts (modules' data, later data sets) go with the copy, as in an archive
    await copyCanvasParts(storage, id, storage, meta.id);
    await refreshCanvases();
    return meta.id;
  }

  async function deleteCanvas(id: Id): Promise<void> {
    if (tabs.value.includes(id)) await closeTab(id);
    await storage.deleteCanvas(id);
    forgetCanvasChat(id);
    forgetCanvasOpen(id);
    logger.info('canvas.delete', { canvasId: id });
    void storage.gcAttachments();
    await refreshCanvases();
  }

  function go(v: View): void {
    navigated = true;
    if (v === 'modules' || v === 'logs') {
      settingsSection.value = v;
      v = 'settings';
    }
    drawerOpen.value = false;
    view.value = v;
    syncHash();
    if (v !== 'editor' && tabs.value.length) void closeAll();
    else if (v === 'home' || v === 'profile') void refreshCanvases();
  }

  function syncHash(): void {
    const h = view.value === 'editor' && active.value ? `#/c/${active.value}` : view.value === 'home' ? '#/' : `#/${view.value}`;
    if (location.hash !== h) history.replaceState(null, '', h);
  }

  async function applyHash(): Promise<void> {
    if (navigated) return;
    const h = location.hash;
    const m = /^#\/c\/([\w]+)$/.exec(h);
    if (m) await openCanvas(m[1], true);
    else if (h === '#/modules') {
      settingsSection.value = 'modules';
      view.value = 'settings';
    }
    else if (h === '#/settings') view.value = 'settings';
    else if (h === '#/profile') view.value = 'profile';
    else if (h === '#/cards') view.value = 'cards';
    else if (h === '#/logs') {
      settingsSection.value = 'logs';
      view.value = 'settings';
    }
    else view.value = 'home';
  }

  return {
    view,
    canvases,
    canvasesLoaded,
    tabs,
    active,
    sessions,
    setCanvasCover,
    sessionHooks,
    toasts,
    drawerOpen,
    settingsSection,
    notify,
    dismiss,
    refreshCanvases,
    createCanvas,
    setCanvasSetting,
    openCanvas,
    closeTab,
    renameCanvas,
    duplicateCanvas,
    deleteCanvas,
    go,
    applyHash,
    setSessionHooks,
    setBeforeClose,
  };
});
